/**
 * Componente para listar os artigos, pode ser usado na página de listagem de artigos
 * Ele busca os artigos do backend de acorgo com o perfil do usuário usando o token de autenticação armazenado no LocalStorage e exibe a lista na tela, caso existam token . Se não existir token vai listar apenas os artigos publicados.
 * Se ocorrer algum erro durante a busca, ele redireciona para a página de resultado com a mensagem de erro.
 * O componente usa o hook useEffect para buscar os artigos assim que for montado, e o hook useState para armazenar a lista de artigos no estado do componente.
 * O hook useNavigate é usado para redirecionar o usuário para a página de resultado em caso de erro.
 * A URL da requisição para buscar os artigos é construída usando a variável de ambiente REACT_APP_API_URL, garantindo que a aplicação possa ser configurada para diferentes ambientes (desenvolvimento, produção, etc.) sem precisar alterar o código.
 *  Estilo básico usando classes do Bootstrap para a lista de artigos.
 */
import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contextos/AuthContext"; // Importa o hook useAuth do contexto de autenticação para acessar o estado do usuário logado e funções relacionadas à autenticação
import fotoIndisponivel from "../../assets/imagens/indisponivel.png";
import CampoTexto from "../CampoTexto";
import MenuLink from "../MenuLink";



const ListaArtigos = () => {
    const location = useLocation(); // Hook para acessar o estado passado pela rota, que pode conter os dados do usuário logado para edição
    const { token } = useAuth(); // reage automaticamente ao login!
    
    //Use o operador de coalescência nula (??). Ele só assume o valor padrão se o dado for estritamente null ou undefined, permitindo que a string vazia passe.
    const statusFiltro = location.state?.status ?? 'publicado'; // Obtém o status de filtro da localização, ou define como 'publicado' se não estiver presente
    //console.log("Status de filtro recebido na Listagem de Artigos:", location.state.status); // Log para verificar o status de filtro recebido na listagem de artigos

    const navigate = useNavigate(); // Hook para navegação programática 
    const [artigos, setArtigos] = useState([]); // Estado para armazenar a lista de artigos, inicialmente vazio
    const [artigosFiltrados, setArtigosFiltrados] = useState([]); // Estado para armazenar a lista de artigos filtrados, inicialmente vazio
    const [termoBusca, setTermoBusca] = useState(''); // Estado para armazenar o termo de busca digitado pelo usuário

/* Esta função roda uma vez quando o componente é montado
/* Carrega a lista de artigos do backend assim que o componente for exibido, garantindo que tenhamos os dados para mostrar na tela */    
useEffect(() => {
    //Criamos uma função assíncrona para buscar os artigos do backend, incluindo o token de autenticação no cabeçalho da requisição. Tem que ser criada dentro do useEffect porque o useEffect não pode ser assíncrono diretamente, mas pode chamar funções assíncronas. 
    const buscarArtigos = async () => {
        
        try{
            // Faz a requisição para o backend para buscar os artigos, incluindo o token no cabeçalho de autorização
            
            //Usa a URL da sua variável de ambiente e completa a URL da requisição para buscar os artigos, incluindo o status de filtro como parâmetro de consulta. O backend pode então usar esse parâmetro para filtrar os artigos de acordo com o status desejado (publicado, rascunho, pendente, etc.). 
            const url = `${process.env.REACT_APP_API_URL}/artigo-list?status=${statusFiltro}`;
   

            const resposta = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    // 3. Envia o token via Header Authorization
                    'Authorization': `Bearer ${token}`
                }
            });


            if (!resposta.ok) {
                    // Se o backend retornar 401 ou 403, algo está errado com a permissão
                    throw new Error(`Erro na requisição: ${resposta.status}`);
                }
            
            const dadosRetorno = await resposta.json();
        
            if (dadosRetorno.status === 'sucesso') {
            // Em React, em vez de chamar uma função 'listarUsuarios', 
            // nós salvamos os dados no estado (useState)

            setArtigos(dadosRetorno.dados); // Salva os artigos no estado para que os dados originalmente recebidos do backend estejam disponíveis para renderização e outras operações
            setArtigosFiltrados(dadosRetorno.dados); // Inicialmente, a lista filtrada é a mesma que a lista completa, mas manteremos essa separação para facilitar a implementação da funcionalidade de busca/filtro posteriormente
            
            
            } else {
                navigate('/resultado', { 
                    state: { 
                    status: dadosRetorno.status,
                    mensagem: dadosRetorno.mensagem
                          }
                    });   
            }
        }catch(error){
            navigate('/resultado', { 
                    state: { 
                    status: "erro",
                    mensagem: "Ocorreu um erro ao buscar os artigos: " + error.message
                          }
                    });
        }
    }
    buscarArtigos(); // Chama a função para buscar os artigos quando o componente for montado


}, []);


/* Função para filtrar os artigos com base no termo de busca digitado pelo usuário, atualizando a lista de artigos exibida na tela conforme o termo de busca é digitado. A função verifica se o termo de busca está presente no título ou na descrição dos artigos, e atualiza a lista filtrada de acordo. Se o campo de busca for limpo, a lista original de artigos é restaurada. */
const filtrarArtigos = (termo) => {
    setTermoBusca(termo); // Atualiza o estado do termo de busca para refletir o valor digitado pelo usuário no campo de busca
    if (termo.trim() === "") {
        setArtigosFiltrados(artigos); // Se limpar, volta a lista original
        return;
    }
    // Filtra os artigos com base no termo de busca, verificando se o termo está presente no título (case-insensitive)
    const resultado = artigos.filter((artigo) => {
        return (
            artigo.titulo.toLowerCase().includes(termo.toLowerCase())
        );
    });


    setArtigosFiltrados(resultado); // AQUI é onde a mágica acontece na tela  
    
}


    if (artigos.length === 0) {
        return (
            <div className="container-fluid justify-content-center">
                <h1 className="h1">Artigos do site</h1>
                <p className="text-start fs-3">Nenhum artigo encontrado.</p>
                
            </div>
        );
    }

    return(
        <div className="container-fluid justify-content-center">
            
            <h1 className="h1">Artigos do site</h1>

            {/* Aqui você pode renderizar a lista de artigos usando os dados que foram buscados */}
                        <div className="container-fluid mb-3 bg-dark-subtle p-3 rounded">
                            <CampoTexto
                                label={''}
                                wrapperClass={'w-100'}
                                className={'w-100 bg-success-subtle p-2 rounded fs-5 fw-bold'}
                                placeholder={'Digite o nome do artigo para buscar'}
                                valor={termoBusca} // Você pode implementar a lógica de busca e armazenar o valor do campo de busca no estado, usando useState, e passar esse valor aqui para controlar o campo de texto
                                aoDigitado={(valor) => filtrarArtigos(valor)} // Você pode implementar a lógica de busca e passar uma função aqui que atualize o estado com o valor do campo de busca, e depois use esse valor para filtrar a lista de artigos exibida na tela
                                editavel={true}
                            >
                             </CampoTexto>        
                        </div>

                    <section className="container-fluid row row-cols-1 row-cols-md-3 g-4 justify-content-center">
                        {artigosFiltrados.map((artigo) => (
                            <article key={artigo.id} className="col">
                                <div className="card h-100">
                                    {/* Exibe o status do artigo usando badges do Bootstrap, com cores diferentes para cada status para que o usuário possa identificar facilmente o estado do artigo */}
                                        {statusFiltro === '' && (
                                            <span className={ `fs-5 badge mt-2 ${artigo.status === 'publicado' ? 'bg-success' : artigo.status === 'bloqueado' ? 'bg-secondary' : artigo.status === 'enviado' ? 'bg-warning' : 'bg-dark'}`}>
                                                {artigo.status.charAt(0).toUpperCase() + artigo.status.slice(1)} {/* Exibe o status com a primeira letra maiúscula */}
                                            </span>
                                        )}
                                    <img 
                                        src={artigo.arquivo ? `${process.env.REACT_APP_API_URL}/public/assets/imagens/artigos/${artigo.arquivo}` : fotoIndisponivel}
                                        alt="Capa do Artigo" 
                                        className="card-img-top" 
                                    />   
                                    <div className="card-body d-flex flex-column">
                                        <h5 className="card-title">{artigo.titulo}</h5>
                                        <p className="card-text">Autor: {artigo.nome}</p>

                                        {/* O MenuLink é um componente personalizado que funciona como um Link do React Router, mas com estilos e funcionalidades adicionais. Ele recebe a prop 'to' para definir a URL de destino, e o conteúdo dentro da tag MenuLink é o texto ou elementos que serão exibidos como link. Neste caso, ele direciona para a página de visualização do artigo específico usando o uuid do artigo na URL. O botão tem a classe 'btn btn-primary mt-auto' para aplicar os estilos do Bootstrap, garantindo que o botão fique alinhado na parte inferior da card (mt-auto) mesmo que o conteúdo da descrição seja curto ou longo. */}   
                                        <MenuLink 
                                                to={`/ver-artigo/${artigo.uuid}`}
                                                className="btn btn-primary mt-auto">
                                            Acessar
                                        </MenuLink>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </section>  

                     
        </div>
    );
}

export default ListaArtigos;